// AnimalQuestionFields — the questions about ONE animal, as both household
// forms ask them (WEL-173): the Animal Addendum form and the pre-lease intake's
// animals section render this same component, so they cannot drift into asking
// different things.
//
// The household first says what kind of animal it is (Dog, Cat, Bird, Small
// caged animal, Reptile, Fish / aquarium, Something else) and then sees ONLY
// that kind's questions from lib/animalQuestions.ts — a dog's breed, weight and
// age; a cat's age and color; a hamster's species and nothing else. Optional
// questions say "(optional)". Nothing here ever shows an id. The staff "record
// on behalf" editor renders it too, so staff-entered animals carry the same
// answers in the same shape.

import {
  ANIMAL_FIELD_ORDER,
  ANIMAL_KIND_CHOICES,
  animalFieldLabel,
  animalFieldPlaceholder,
  type AnimalAgeUnit,
  type AnimalField,
  type AnimalGap,
  type AnimalKindChoice,
} from '@/lib/animalQuestions';
import { asksDraftField, draftQuestionKind, type AnimalAnswerDraft } from '@/lib/animalDraft';

export function AnimalQuestionFields(props: {
  /** Prefix for the inputs' ids (unique per card on the page). */
  idPrefix: string;
  value: AnimalAnswerDraft;
  disabled?: boolean;
  onChange: (patch: Partial<AnimalAnswerDraft>) => void;
  /** The helper under a blank license number (each form words its own). */
  licenseHelper: string;
  /**
   * Answers to mark invalid (aria-invalid) — the staff record form highlights
   * what is still missing after a Record attempt. 'type' / 'otherKind' mark the
   * kind choice. The household forms say it in words instead.
   */
  invalid?: ReadonlySet<AnimalGap>;
  /**
   * RRA v2.2 part B: a COVERED animal listed by the server — its name (and a dog or cat's type) is
   * fixed so the answers stay paired with it. The household still completes everything else.
   */
  locked?: { name?: boolean; kind?: boolean };
}): React.JSX.Element {
  const { idPrefix, value: a, disabled = false, onChange } = props;
  const kind = draftQuestionKind(a);
  const id = (field: string): string => `${idPrefix}-${field}`;
  const flag = (gap: AnimalGap): { 'aria-invalid'?: 'true' } =>
    props.invalid?.has(gap) ? { 'aria-invalid': 'true' } : {};

  const textInput = (
    field: AnimalField & ('otherDescription' | 'breed' | 'color' | 'licenseNumber'),
    wide = false,
  ): React.JSX.Element => (
    <div key={field} className="field" style={wide ? { gridColumn: '1 / -1' } : undefined}>
      <label htmlFor={id(field)}>{animalFieldLabel(kind, field)}</label>
      <input
        id={id(field)}
        className="input"
        placeholder={animalFieldPlaceholder(kind, field) || undefined}
        value={a[field]}
        disabled={disabled}
        {...flag(field)}
        onChange={(e) => onChange({ [field]: e.target.value })}
      />
      {field === 'licenseNumber' && !a.licenseNumber.trim() ? (
        <span className="helper">{props.licenseHelper}</span>
      ) : null}
    </div>
  );

  const numberInput = (field: 'weightLbs' | 'tankGallons'): React.JSX.Element => (
    <div key={field} className="field">
      <label htmlFor={id(field)}>{animalFieldLabel(kind, field)}</label>
      <input
        id={id(field)}
        className="input"
        inputMode="decimal"
        placeholder={animalFieldPlaceholder(kind, field) || undefined}
        value={a[field]}
        disabled={disabled}
        {...flag(field)}
        onChange={(e) => onChange({ [field]: e.target.value })}
      />
    </div>
  );

  const fields: React.JSX.Element[] = [];
  for (const field of ANIMAL_FIELD_ORDER) {
    // Name leads the card; the photo is the addendum form's own block.
    if (field === 'name' || field === 'photo' || !asksDraftField(a, field)) continue;
    switch (field) {
      case 'otherDescription':
        fields.push(textInput('otherDescription', true));
        break;
      case 'breed':
      case 'color':
      case 'licenseNumber':
        fields.push(textInput(field));
        break;
      case 'weightLbs':
      case 'tankGallons':
        fields.push(numberInput(field));
        break;
      case 'age':
        fields.push(
          <div key="age" className="field">
            <label htmlFor={id('age')}>{animalFieldLabel(kind, 'age')}</label>
            <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
              <input
                id={id('age')}
                className="input"
                inputMode="decimal"
                value={a.age}
                disabled={disabled}
                {...flag('age')}
                style={{ flex: 1, minWidth: 0 }}
                onChange={(e) => onChange({ age: e.target.value })}
              />
              <select
                className="input"
                aria-label="Age in years or months"
                value={a.ageUnit}
                disabled={disabled}
                style={{ width: 'auto' }}
                onChange={(e) => onChange({ ageUnit: e.target.value as AnimalAgeUnit })}
              >
                <option value="years">years</option>
                <option value="months">months</option>
              </select>
            </div>
          </div>,
        );
        break;
      case 'gender':
        fields.push(
          <div key="gender" className="field">
            <label htmlFor={id('gender')}>{animalFieldLabel(kind, 'gender')}</label>
            <select
              id={id('gender')}
              className="input"
              value={a.gender}
              disabled={disabled}
              onChange={(e) => onChange({ gender: e.target.value as 'male' | 'female' | '' })}
            >
              <option value="">Select…</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
            </select>
          </div>,
        );
        break;
      case 'spayedNeutered':
        fields.push(
          <div key="spayedNeutered" className="field">
            <label htmlFor={id('spayedNeutered')}>{animalFieldLabel(kind, 'spayedNeutered')}</label>
            <select
              id={id('spayedNeutered')}
              className="input"
              value={a.spayedNeutered === null ? '' : a.spayedNeutered ? 'yes' : 'no'}
              disabled={disabled}
              onChange={(e) =>
                onChange({
                  spayedNeutered: e.target.value === '' ? null : e.target.value === 'yes',
                })
              }
            >
              <option value="">Select…</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>,
        );
        break;
    }
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
      <div className="field">
        <label htmlFor={id('name')}>{animalFieldLabel(kind, 'name')}</label>
        <input
          id={id('name')}
          className="input"
          value={a.name}
          disabled={disabled}
          {...(props.locked?.name ? { readOnly: true } : {})}
          {...flag('name')}
          onChange={(e) => onChange({ name: e.target.value })}
        />
      </div>
      <div className="field">
        <label htmlFor={id('kind')}>Type</label>
        <select
          id={id('kind')}
          className="input"
          value={a.kind}
          disabled={disabled || props.locked?.kind === true}
          {...(props.invalid?.has('type') || props.invalid?.has('otherKind')
            ? { 'aria-invalid': 'true' as const }
            : {})}
          onChange={(e) => onChange({ kind: e.target.value as AnimalKindChoice | '' })}
        >
          <option value="">Select…</option>
          {ANIMAL_KIND_CHOICES.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </select>
      </div>
      {fields}
    </div>
  );
}
